import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: #000;
  z-index: 9999;
`;

const BackButtonContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  display: flex;
  gap: 10px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: linear-gradient(135deg, rgba(103, 82, 232, 0.98), rgba(118, 75, 162, 0.98));
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;
  box-shadow: 0 8px 24px rgba(103, 82, 232, 0.4);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;

  &:hover {
    background: linear-gradient(135deg, rgba(103, 82, 232, 1), rgba(118, 75, 162, 1));
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(103, 82, 232, 0.5);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 16px rgba(103, 82, 232, 0.4);
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
    bottom: 15px;
  }
`;

const BackIcon = styled.span`
  font-size: 18px;
  font-weight: bold;
`;

const IframeWrapper = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100vh;
  border: none;
  display: block;
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  // background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const Spinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 4px solid white;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.p`
  margin-top: 20px;
  font-size: 18px;
  font-weight: 500;
`;

const ErrorButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  background: white;
  color: #667eea;
  border: none;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

interface ParliamentRedirectProps {
  parliamentName: string;
}

const ParliamentRedirect: React.FC<ParliamentRedirectProps> = ({ parliamentName }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [parliamentUrl, setParliamentUrl] = useState<string>('');

  useEffect(() => {
    // Try to get URL from query params first
    let url = searchParams.get('url');

    // If not in params, try sessionStorage
    if (!url) {
      const storageKey = `parliament_url_${location.pathname}`;
      url = sessionStorage.getItem(storageKey);
    } else {
      // Store in sessionStorage and navigate to clean URL
      const storageKey = `parliament_url_${location.pathname}`;
      sessionStorage.setItem(storageKey, url);

      // Navigate to clean URL without query params
      navigate(location.pathname, { replace: true });
    }

    if (!url) {
      setError(true);
      setLoading(false);
      return;
    }

    setParliamentUrl(url);

    // Simulate loading for smooth transition
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchParams, location.pathname, navigate]);

  const handleGoBack = () => {
    // Clear sessionStorage for this parliament
    const storageKey = `parliament_url_${location.pathname}`;
    sessionStorage.removeItem(storageKey);
    navigate('/apps');
  };

  if (error || !parliamentUrl) {
    return (
      <LoaderContainer>
        <Message>❌ Unable to load {parliamentName}</Message>
        <ErrorButton onClick={handleGoBack}>Go Back to Apps</ErrorButton>
      </LoaderContainer>
    );
  }

  if (loading) {
    return (
      <LoaderContainer>
        <Spinner />
        <Message>Loading {parliamentName}...</Message>
      </LoaderContainer>
    );
  }

  return (
    <Container>
      <BackButtonContainer>
        <BackButton onClick={handleGoBack}>
          <BackIcon>←</BackIcon>
          Back to V2E
        </BackButton>
      </BackButtonContainer>
      <IframeWrapper>
        <StyledIframe
          src={parliamentUrl}
          title={parliamentName}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </IframeWrapper>
    </Container>
  );
};

export default ParliamentRedirect;
